import React, { Fragment, useContext } from "react";
import { RequirementsProps } from "../../../../tools/context";
import { useTranslation } from "../../../../tools/i18n";
import { getLanguageForConfig } from "../../../../tools/languageUtils";
import { capitalize, getFormattedEuro } from "../../../../tools/tools";
import { getKnowMoreLink } from "../../../../tools/utils";
import Separator from "../../../Separator";
import { getBrandBadgeStyle, getBrandLabel } from "../../brands";
import { TravelPlanerResultsContext } from "../../TravelPlanerResultsContext";
import TravelNightCarousel from "../TravelNightCarousel";
import TravelNightTags, { TravelNightTag } from "../TravelNightTags";

interface TravelNightPointProps {
  requirements: RequirementsProps;
  nightData: any;
}

const HUTTOPIA_TAG_TRANSLATIONS: Record<string, Record<string, string>> = {
  Déconnexion: { en: "Disconnection", de: "Auszeit" },
  Intimité: { en: "Privacy", de: "Privatsphäre" },
  "Nature sauvage": { en: "Wild nature", de: "Wilde Natur" },
  Campagne: { en: "Countryside", de: "Landschaft" },
  Forêt: { en: "Forest", de: "Wald" },
  Localisation: { en: "Location", de: "Lage" },
  "Vue lac": { en: "Lake view", de: "Seeblick" },
  "Localisation exceptionnelle": {
    en: "Exceptional location",
    de: "Außergewöhnliche Lage",
  },
  Vue: { en: "View", de: "Aussicht" },
  Rivière: { en: "River", de: "Fluss" },
  Bivouac: { en: "Bivouac", de: "Biwak" },
  Camping: { en: "Camping", de: "Camping" },
  Bareground: { en: "Bareground", de: "Bareground" },
  Huttopia: { en: "Huttopia", de: "Huttopia" },
  Citykamp: { en: "CityKamp", de: "CityKamp" },
};

const translateTag = (tag: string, language: string): string => {
  const baseLang = (language || "fr").split(/[-_]/)[0];
  if (baseLang === "fr") return tag;
  const trimmed = tag.trim();
  return HUTTOPIA_TAG_TRANSLATIONS[trimmed]?.[baseLang] ?? tag;
};

const TravelNightPoint: React.FC<TravelNightPointProps> = ({
  nightData,
  requirements,
}) => {
  const { t, language } = useTranslation();
  const { config } = useContext(TravelPlanerResultsContext);
  const baseLanguage =
    language ||
    (typeof navigator !== "undefined"
      ? navigator.language.split("-")[0]
      : "fr");
  const currentLanguage = getLanguageForConfig(
    requirements?.config?.key,
    baseLanguage,
  );

  const description =
    nightData?.[`description_${currentLanguage}`] ??
    (nightData?.description || nightData?.description_en);

  const tagsIconsMap = {
    "Vue lac": "waterDrop",
    "Localisation exceptionnelle": "exceptionalLocation",
  };

  const formattedTags =
    `${nightData?.interests ?? ""}|${nightData?.services ?? ""}`
      .replace(/\|\|+/g, "|")
      ?.split("|")
      ?.concat(nightData?.tags)
      ?.filter((i) => !!i)
      ?.filter((i, index, self) => self.indexOf(i) === index)
      ?.map((interest: string) => interest.trim())
      ?.map((interest: string) => ({
        icon: tagsIconsMap?.[interest],
        title: translateTag(interest, baseLanguage),
      }))
      ?.filter((i) => !!i.title) as TravelNightTag[];

  const openingTags = [
    {
      icon: "mapleLeaf",
      title: "Ouverture",
      content: nightData?.opening_date,
    },
  ]?.filter((i) => !!i.content) as TravelNightTag[];

  const fromPriceText = nightData?.price
    ? t("travelPlaner.Travel.TravelNightPoint.priceAndNight", {
        price: getFormattedEuro(nightData?.price),
        night: nightData?.nightsPerSpot,
      })
    : null;

  let carouselImages =
    nightData?.images ||
    (nightData?.urlimg
      ? [nightData?.urlimg]
      : [config?.siteFallbackImage?.image]);

  if (carouselImages.length < 3)
    carouselImages = Array(3).fill(carouselImages[0]);

  const link = getKnowMoreLink(requirements, nightData);

  return (
    <div className="TravelNightPoint">
      <TravelNightCarousel
        requirements={requirements}
        images={carouselImages}
      />
      <div className="TravelNightPointContainer">
        <div className="TravelNightPointHeaderContainer">
          <div className="TravelNightPointHeader">
            <h4>
              {`${nightData?.dept ?? nightData?.city}${nightData?.dept_nb ? ` (${nightData?.dept_nb})` : ""}`}
            </h4>
            {nightData?.brand ? (
              <span style={getBrandBadgeStyle(nightData.brand)}>
                {getBrandLabel(nightData.brand) || capitalize(nightData.brand)}
              </span>
            ) : null}
          </div>
          <div className="TravelNightPointSubHeader">
            {fromPriceText ? (
              <span dangerouslySetInnerHTML={{ __html: fromPriceText }} />
            ) : null}
          </div>
        </div>
        {formattedTags?.length ? (
          <Fragment>
            <Separator />
            <TravelNightTags tags={formattedTags} />
          </Fragment>
        ) : null}
        {!!description ? (
          <Fragment>
            <Separator />
            <p dangerouslySetInnerHTML={{ __html: description }} />
          </Fragment>
        ) : null}
        {openingTags?.length ? (
          <Fragment>
            <Separator />
            <TravelNightTags tags={openingTags} />
          </Fragment>
        ) : null}
      </div>
    </div>
  );
};

export default TravelNightPoint;
